import React, { useState } from 'react';
import { Form, Input, Upload, Button } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import LoadableButton from '../../../../components/buttons/LoadableButton';
import { CreateTermsAndConditionApi } from '../../../../api/request/tnc';

const AddTNC = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [submitLoading, setSubmitLoading] = useState(false);
    const [fileUploads, setFileUploads] = useState({
        BonusTermsConditions: [],
        ClientAgreement: [],
        ComplaintHandlingPolicy: [],
        ConfidentialityPolicy: [],
        GeneralTermsConditionsPolicy: [],
        KeyFactsStatement: [],
        PartnershipAgreement: []
    });

    // File upload validation
    const beforeUpload = (file, fieldName) => {
        const isPDF = file.type === 'application/pdf';
        if (!isPDF) {
            toast.error('You can only upload PDF files!');
            return false;
        }
        const isLt10M = file.size / 1024 / 1024 < 10;
        if (!isLt10M) {
            toast.error('File must be smaller than 10MB!');
            return false;
        }
        return false; // Prevent automatic upload
    };

    // Handle file change for each upload field
    const handleFileChange = (fieldName) => ({ fileList: newFileList }) => {
        setFileUploads(prev => ({ ...prev, [fieldName]: newFileList }));
    };

    // Form submission handler
    const handleSubmit = async (values) => {
        setSubmitLoading(true);
        try {
            // Create FormData for multipart/form-data
            const formData = new FormData();
            formData.append('Title', values.Title);
            formData.append('Version', values.Version);
            // Append PDF files if selected
            const fileFields = [
                { key: 'BonusTermsConditions', name: 'BonusTermsConditions' },
                { key: 'ClientAgreement', name: 'ClientAgreement' },
                { key: 'ComplaintHandlingPolicy', name: 'ComplaintHandlingPolicy' },
                { key: 'ConfidentialityPolicy', name: 'ConfidentialityPolicy' },
                { key: 'GeneralTermsConditionsPolicy', name: 'GeneralTermsConditionsPolicy' },
                { key: 'KeyFactsStatement', name: 'KeyFactsStatement' },
                { key: 'PartnershipAgreement', name: 'PartnershipAgreement' }
            ];
            fileFields.forEach(field => {
                if (fileUploads[field.key].length > 0 && fileUploads[field.key][0].originFileObj) {
                    formData.append(field.name, fileUploads[field.key][0].originFileObj);
                }
            });
            // API call
            const response = await CreateTermsAndConditionApi(formData, {
                headers: { Accept: "application/json", "Content-Type": "multipart/form-data" }
            });

            if (response.data?.success) {
                toast.success(response.data?.message);
                form.resetFields();
                setFileUploads({
                    BonusTermsConditions: [],
                    ClientAgreement: [],
                    ComplaintHandlingPolicy: [],
                    ConfidentialityPolicy: [],
                    GeneralTermsConditionsPolicy: [],
                    KeyFactsStatement: [],
                    PartnershipAgreement: []
                });

            } else {
                toast.error(response.data?.message);
            }

        } catch (error) {
            toast.error(error?.response?.data?.error?.message);
        } finally {
            setSubmitLoading(false);
        }
    };



    return (
        <div className="border rounded-md p-4 mt-3">
            <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off" requiredMark={false} className='space-y-6'>
                <div className='grid grid-cols-3 gap-4 border rounded-md p-4'>
                    <Form.Item
                        label="Title"
                        name="Title"
                        rules={[{ required: true, message: 'Please enter title' },]}
                    >
                        <Input size="large" placeholder="Title" />
                    </Form.Item>
                    <Form.Item
                        label="Version"
                        name="Version"
                        rules={[{ required: true, message: 'Please enter version' },]}
                    >
                        <Input size="large" placeholder="Version" />
                    </Form.Item>
                </div>
                <div className='grid grid-cols-3 gap-4 border rounded-md p-4'>
                    <Form.Item label="Bonus" name="BonusTermsConditions">
                        <Upload
                            fileList={fileUploads.BonusTermsConditions}
                            beforeUpload={(file) => beforeUpload(file, 'BonusTermsConditions')}
                            onChange={handleFileChange('BonusTermsConditions')}
                            accept=".pdf"
                            maxCount={1}
                            className="w-full"
                        >
                            <Button icon={<UploadOutlined />} size="large" className="w-full">
                                Upload Bonus Terms PDF
                            </Button>
                        </Upload>
                        {fileUploads.BonusTermsConditions.length > 0 && (
                            <div className="mt-2 text-sm text-green-600">
                                ✓ {fileUploads.BonusTermsConditions[0].name}
                            </div>
                        )}
                    </Form.Item>
                    <Form.Item label="Client Agreement" name="ClientAgreement" >
                        <Upload
                            fileList={fileUploads.ClientAgreement}
                            beforeUpload={(file) => beforeUpload(file, 'ClientAgreement')}
                            onChange={handleFileChange('ClientAgreement')}
                            accept=".pdf"
                            maxCount={1}
                            className="w-full"
                        >
                            <Button icon={<UploadOutlined />} size="large" className="w-full">
                                Upload Client Agreement PDF
                            </Button>
                        </Upload>
                        {fileUploads.ClientAgreement.length > 0 && (
                            <div className="mt-2 text-sm text-green-600">
                                ✓ {fileUploads.ClientAgreement[0].name}
                            </div>
                        )}
                    </Form.Item>
                    <Form.Item label="Complaint Handling Policy" name="ComplaintHandlingPolicy" >
                        <Upload
                            fileList={fileUploads.complaintHandling}
                            beforeUpload={(file) => beforeUpload(file, 'ComplaintHandlingPolicy')}
                            onChange={handleFileChange('ComplaintHandlingPolicy')}
                            accept=".pdf"
                            maxCount={1}
                            className="w-full"
                        >
                            <Button icon={<UploadOutlined />} size="large" className="w-full">
                                Upload Complaint Handling PDF
                            </Button>
                        </Upload>
                        {fileUploads.ComplaintHandlingPolicy.length > 0 && (
                            <div className="mt-2 text-sm text-green-600">
                                ✓ {fileUploads.ComplaintHandlingPolicy[0].name}
                            </div>
                        )}
                    </Form.Item>
                    <Form.Item label="Confidentiality Policy" name="ConfidentialityPolicy" >
                        <Upload
                            fileList={fileUploads.ConfidentialityPolicy}
                            beforeUpload={(file) => beforeUpload(file, 'ConfidentialityPolicy')}
                            onChange={handleFileChange('ConfidentialityPolicy')}
                            accept=".pdf"
                            maxCount={1}
                            className="w-full"
                        >
                            <Button icon={<UploadOutlined />} size="large" className="w-full">
                                Upload Confidentiality Policy PDF
                            </Button>
                        </Upload>
                        {fileUploads.ConfidentialityPolicy.length > 0 && (
                            <div className="mt-2 text-sm text-green-600">
                                ✓ {fileUploads.ConfidentialityPolicy[0].name}
                            </div>
                        )}
                    </Form.Item>
                    <Form.Item label="General Terms & Conditions" name="GeneralTermsConditionsPolicy" >
                        <Upload
                            fileList={fileUploads.GeneralTermsConditionsPolicy}
                            beforeUpload={(file) => beforeUpload(file, 'GeneralTermsConditionsPolicy')}
                            onChange={handleFileChange('GeneralTermsConditionsPolicy')}
                            accept=".pdf"
                            maxCount={1}
                            className="w-full"
                        >
                            <Button icon={<UploadOutlined />} size="large" className="w-full">
                                Upload General Terms PDF
                            </Button>
                        </Upload>
                        {fileUploads.GeneralTermsConditionsPolicy.length > 0 && (
                            <div className="mt-2 text-sm text-green-600">
                                ✓ {fileUploads.GeneralTermsConditionsPolicy[0].name}
                            </div>
                        )}
                    </Form.Item>
                    <Form.Item label="Key Facts Statement" name="KeyFactsStatement">
                        <Upload
                            fileList={fileUploads.KeyFactsStatement}
                            beforeUpload={(file) => beforeUpload(file, 'KeyFactsStatement')}
                            onChange={handleFileChange('KeyFactsStatement')}
                            accept=".pdf"
                            maxCount={1}
                            className="w-full"
                        >
                            <Button icon={<UploadOutlined />} size="large" className="w-full">
                                Upload Key Facts Statement PDF
                            </Button>
                        </Upload>
                        {fileUploads.KeyFactsStatement.length > 0 && (
                            <div className="mt-2 text-sm text-green-600">
                                ✓ {fileUploads.KeyFactsStatement[0].name}
                            </div>
                        )}
                    </Form.Item>
                    <Form.Item label="Partnership Agreement" name="PartnershipAgreement" >
                        <Upload
                            fileList={fileUploads.PartnershipAgreement}
                            beforeUpload={(file) => beforeUpload(file, 'PartnershipAgreement')}
                            onChange={handleFileChange('PartnershipAgreement')}
                            accept=".pdf"
                            maxCount={1}
                            className="w-full"
                        >
                            <Button icon={<UploadOutlined />} size="large" className="w-full">
                                Upload Partnership Agreement PDF
                            </Button>
                        </Upload>
                        {fileUploads.PartnershipAgreement.length > 0 && (
                            <div className="mt-2 text-sm text-green-600">
                                ✓ {fileUploads.PartnershipAgreement[0].name}
                            </div>
                        )}
                    </Form.Item>
                </div>
                <div className="flex justify-end gap-4">
                    <button type='button' onClick={() => navigate('/terms')} className="border border-primary primary rounded-md px-3">Cancel</button>
                    <LoadableButton
                        type="submit"
                        isLoading={submitLoading}
                        lable="Create Terms & Conditions"
                        loadingLable="Creating..."
                        className="bg-primary text-white px-3 py-2 rounded-lg"
                        size="large"
                    />
                </div>
            </Form>
        </div>

    );
};

export default AddTNC;